import { useMutation } from '@tanstack/react-query';
import { isEmpty, take } from 'rambda';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useContacts from '@/hooks/contacts/use-contacts';
import type { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import type { RespondedWith, ResponseError } from '@/models/response';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import type { SessionKey } from '@/utils/session';

async function forwardMessage(params: ForwardMessageRequest): Promise<RespondedWith<ForwardErrorResponseSchema>> {
  const { userIds, groupIds } = generateForwardTargetIds(params.targets);

  if ('messageIds' in params && !isEmpty(params.messageIds)) {
    const messages = params.messageIds
      .map((messageId) => ({
        GroupID: params.sessionType === 'group' ? params.sessionId : 0,
        ChatSessionID: params.sessionType === 'session' ? params.sessionId : 0,
        ChatID: messageId,
      }))
      .filter((m) => m.GroupID || m.ChatSessionID);

    return fetcher(
      '/ForwardMessages',
      {
        Messages: messages,
        UserIDs: userIds,
        GroupIDs: groupIds,
      },
      { timeout: 60_000, validResponseCodes: [0, 1] },
    );
  }

  if ('urls' in params && !isEmpty(params.urls)) {
    return fetcher(
      '/ForwardMessages',
      {
        Urls: params.urls.map(({ url, type }) => ({
          Url: url,
          Type: type,
        })),
        UserIDs: userIds,
        GroupIDs: groupIds,
      },
      { timeout: 60_000, validResponseCodes: [0, 1] },
    );
  }

  if ('collectionIds' in params && !isEmpty(params.collectionIds)) {
    return fetcher(
      '/ForwardMessages',
      {
        CollectIDs: params.collectionIds,
        UserIDs: userIds,
        GroupIDs: groupIds,
      },
      { timeout: 60_000, validResponseCodes: [0, 1] },
    );
  }

  throw new Error('BAD_FORWARD_REQUEST');
}

export default function useBatchForward() {
  const { t } = useTranslation();
  const { friends, groups } = useContacts();

  const mutation = useMutation<RespondedWith<ForwardErrorResponseSchema>, ResponseError, ForwardMessageRequest>({
    mutationKey: ['/ForwardMessages'],
    mutationFn: (params) => forwardMessage(params),
    retry: false,
    onSuccess: (data) => {
      if (data.Code === 0) {
        addToast({
          type: 'success',
          message: t('conversation.forward.forward-success'),
        });
      } else if (data.Code === 1 && Array.isArray(data.Data)) {
        const names: string[] = [];

        for (const result of data.Data) {
          for (const user of result.UserResults) {
            const f = friends[user.UserID];
            if (f) {
              names.push(f.RemarkName || f.FriendNickName);
            }
          }
          for (const group of result.GroupResults) {
            const g = groups[group.GroupID];
            if (g) {
              names.push(g.GroupName);
            }
          }
        }

        makePrompt({
          key: 'forward-messages',
          resolveButtonType: 'informative',
          title: t('conversation.forward.forward-fail'),
          content: names.join(', '),
        });
      }
    },
  });

  const mutateAsyncWithPrompt = useCallback(
    (request: ForwardMessageRequest) =>
      makePrompt({
        key: 'forward-messages',
        title: t('my-collection.confirm-forward', {
          count: request.targets.length,
          user: take(3, request.targets)
            .map((s) => s.name)
            .join(', '),
        }),
        promise: () => mutation.mutateAsync(request),
      }),
    [mutation.mutateAsync, t],
  );

  return {
    ...mutation,
    mutateAsyncWithPrompt,
  };
}

function generateForwardTargetIds(targets: ForwardTarget[]) {
  const result: ForwardTargetIds = { userIds: [], groupIds: [] };

  for (const target of targets) {
    if ('userId' in target && target.userId) {
      result.userIds.push(target.userId);
    } else if ('groupId' in target && target.groupId) {
      result.groupIds.push(target.groupId);
    } else if ('tagId' in target && target.tagId) {
      result.userIds.push(...target.userIds);
    }
  }

  return result;
}

export type ForwardTarget = {
  key: SessionKey | `tag-${number}`;
  name: string;
} & (
  | { type: 'group'; groupId: number }
  | { type: 'user'; userId: number }
  | { type: 'tag'; tagId: number; userIds: number[] }
);

type ForwardTargetIds = {
  userIds: number[];
  groupIds: number[];
};

type ForwardMessageRequest =
  | {
      sessionType: ConversationType;
      sessionId: number;
      messageIds: number[];
      targets: ForwardTarget[];
    }
  | {
      collectionIds: number[];
      targets: ForwardTarget[];
    }
  | {
      urls: Array<{ url: string; type: ConversationMessageContentType }>;
      targets: ForwardTarget[];
    };

const userResultScheme = z.object({
  UserID: z.number(),
  Success: z.boolean(),
  Msg: z.string(),
});

const groupResultScheme = z.object({
  GroupID: z.number(),
  Success: z.boolean(),
  Msg: z.string(),
});

const forwardErrorSchema = z.object({
  ChatID: z.number(),
  ChatSessionID: z.number(),
  GroupID: z.number(),
  CollectID: z.number(),
  Error: z.string(),
  UserResults: z.array(userResultScheme),
  GroupResults: z.array(groupResultScheme),
});

export type ForwardErrorResponseSchema = z.infer<typeof forwardErrorSchema>;
